/**
 * 统一路由变更管理器
 * 集中代理 pushState/replaceState，监听 popstate/hashchange
 * 通过订阅机制将路由变化事件分发给各模块
 */

/** 路由变更信息 */
export interface RouteChangeInfo {
  /** 变更前的 URL */
  oldUrl: string
  /** 变更后的 URL */
  newUrl: string
  /** 触发来源 */
  trigger: 'pushState' | 'replaceState' | 'popstate' | 'hashchange'
}

/** 路由变更回调 */
export type RouteChangeCallback = (info: RouteChangeInfo) => void

/** 取消订阅函数 */
export type Unsubscribe = () => void

/**
 * 路由变更管理器
 */
export class RouteChangeManager {
  /** 调试模式 */
  private debug: boolean
  /** 是否已初始化 */
  private isInitialized = false
  /** 上次 URL（用于去重） */
  private lastUrl = ''
  /** 订阅者集合 */
  private subscribers = new Set<RouteChangeCallback>()

  /** 保存原始 history 方法 */
  private originalPushState: typeof history.pushState | null = null
  private originalReplaceState: typeof history.replaceState | null = null

  /** 事件处理器引用（用于 destroy 时移除） */
  private boundPopstateHandler: (() => void) | null = null
  private boundHashchangeHandler: (() => void) | null = null

  constructor(debug?: boolean) {
    this.debug = debug ?? false
  }

  /**
   * 初始化：代理 pushState/replaceState + 绑定 popstate/hashchange
   */
  init(): void {
    if (this.isInitialized) return
    if (typeof window === 'undefined') return

    this.isInitialized = true
    this.lastUrl = location.href

    // 代理 pushState
    this.originalPushState = history.pushState
    history.pushState = (...args) => {
      this.originalPushState?.apply(history, args)
      this.handleRouteChange('pushState')
    }

    // 代理 replaceState
    this.originalReplaceState = history.replaceState
    history.replaceState = (...args) => {
      this.originalReplaceState?.apply(history, args)
      this.handleRouteChange('replaceState')
    }

    // 监听 popstate（浏览器前进/后退）
    this.boundPopstateHandler = () => this.handleRouteChange('popstate')
    window.addEventListener('popstate', this.boundPopstateHandler)

    // 监听 hashchange（hash 路由）
    this.boundHashchangeHandler = () => this.handleRouteChange('hashchange')
    window.addEventListener('hashchange', this.boundHashchangeHandler)

    this.log('RouteChangeManager 已初始化')
  }

  /**
   * 注册路由变更回调
   * @returns 取消订阅函数
   */
  subscribe(callback: RouteChangeCallback): Unsubscribe {
    this.subscribers.add(callback)
    this.log('Subscriber added', { count: this.subscribers.size })

    return () => {
      this.subscribers.delete(callback)
      this.log('Subscriber removed', { count: this.subscribers.size })
    }
  }

  /**
   * 获取当前 URL
   */
  getCurrentUrl(): string {
    return typeof location !== 'undefined' ? location.href : ''
  }

  /**
   * 销毁：恢复原始方法、移除监听、清空订阅者
   */
  destroy(): void {
    if (!this.isInitialized) return

    // 移除事件监听
    if (this.boundPopstateHandler) {
      window.removeEventListener('popstate', this.boundPopstateHandler)
    }
    if (this.boundHashchangeHandler) {
      window.removeEventListener('hashchange', this.boundHashchangeHandler)
    }

    // 恢复原始 history 方法
    if (this.originalPushState) {
      history.pushState = this.originalPushState
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState
    }

    // 清空状态
    this.subscribers.clear()
    this.originalPushState = null
    this.originalReplaceState = null
    this.boundPopstateHandler = null
    this.boundHashchangeHandler = null
    this.isInitialized = false

    this.log('RouteChangeManager destroyed')
  }

  /**
   * 统一处理路由变化
   */
  private handleRouteChange(trigger: RouteChangeInfo['trigger']): void {
    const newUrl = location.href
    if (newUrl === this.lastUrl) return

    const oldUrl = this.lastUrl
    this.lastUrl = newUrl

    const info: RouteChangeInfo = { oldUrl, newUrl, trigger }

    this.log('路由变更', info)

    // 快照遍历，防止回调中修改订阅者集合导致不确定行为
    const snapshot = [...this.subscribers]
    for (const callback of snapshot) {
      try {
        callback(info)
      } catch (err) {
        // console.error('[Tracker RouteChange] Subscriber error:', err)
      }
    }
  }

  /**
   * 调试
   */
  private log(_message: string, _data?: unknown): void {
    if (this.debug) {
      // console.log('%c[Tracker RouteChange 调试] %c ' + _message, 'background: #e2b5b5ff; color: #fff;', 'color: inherit;', _data)
    }
  }
}
